import { FC, SelectHTMLAttributes } from 'react';
import { Controller } from 'react-hook-form';
import { FormBaseLabel } from './FormBaseLabel';
import { FormSelectOptions } from './FormSelectOptions';

/**
 * Props interface for the FormInputField component.
 * It extends the attributes available for an HTML select element
 */
export interface Props extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  name: string; // The name of the field which is used to register with react-hook-form
  control: any; // The control object from useForm hook
  options: { value: string; label: string; selected?: boolean }[]; // Array of options for the select component
  error?: string | null;
}

/**
 * FormSelect component is a custom form select component with built-in validation and tooltips.
 * @param props - The properties specific to the FormSelect component.
 * @returns A fully functional select component with integrated label, error message, and select options.
 */
const FormSelect: FC<Props> = ({ label, name, control, options, error, ...rest }) => {
  return (
    <div className="flex w-full flex-col gap-1">
      <FormBaseLabel id={name} label={label} error={error} />
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <select
            className={`h-[40px] rounded-lg border-[1px] ${
              error ? 'border-red-500' : 'border-[#CED4DA]'
            } px-4 text-sm outline-none focus:border-blue-700`}
            {...field}
            {...rest}
          >
            <FormSelectOptions options={options} />
          </select>
        )}
      />
    </div>
  );
};

export { FormSelect };
